import type { PermissionTuple } from '~/utils/ability'
import type { PermissionRequirement, RouteTo } from '~/utils/rbac'
import { moduleIndexPermission } from '~/utils/rbacModuleIndex'
import type { GuardedTab } from '~/utils/rbacTabs'

export type UsersTab = 'app' | 'payment-defaults' | 'bo'

/**
 * Source unique consommée par le `staticData.permission` de chaque route d'onglet, par la barre
 * d'onglets et par `/users` : pas de dérive possible entre l'onglet affiché et la page qu'il ouvre.
 */
export const USERS_TAB_PERMISSIONS = {
  app: ['read', 'users.app-users.user'],
  'payment-defaults': ['read', 'users.payment-defaults.default'],
  bo: ['read', 'users.bo-users.user'],
} as const satisfies Record<UsersTab, PermissionTuple>

type UsersTabDefinition = GuardedTab<UsersTab> & {
  to: RouteTo
  permission: PermissionTuple
}

/**
 * Ordre de déclaration = ordre d'affichage, et ordre de repli de la redirection `/users`.
 *
 * Le module 7 ne porte plus que les **utilisateurs de l'app** : les comptes du back-office et les
 * rôles vivent sous Administration (`/administration/access`), parce que `users.bo-users.*` et
 * `users.app-users.*` ne s'ouvrent pas aux mêmes personnes. L'onglet « Utilisateurs du BO » a donc
 * quitté cette barre, et `/users/bo` ne sert plus que de redirection.
 */
export const USERS_TABS: UsersTabDefinition[] = [
  {
    value: 'app',
    label: "Utilisateurs de l'app",
    to: '/users/app',
    permission: USERS_TAB_PERMISSIONS.app,
  },
  {
    value: 'payment-defaults',
    label: 'Défauts de paiement',
    to: '/users/payment-defaults',
    permission: USERS_TAB_PERMISSIONS['payment-defaults'],
  },
  // `/users/bo` a quitté la barre : sa DataGrid MUI des comptes est remplacée par l'axe
  // « Par utilisateur » de `/administration/access` (spec §3.1).
]

/**
 * Accès au module : dès qu'un seul de ses écrans est accessible.
 *
 * La création d'un compte BO n'y figure plus — elle est passée sous Administration
 * (`/administration/admins/new`), où c'est l'entrée « Administration » qui applique la règle
 * « toute entrée visible mène à un écran ouvrable » (audit du 19/09, MAJ-07).
 */
export const USERS_PAGE_READ: PermissionRequirement = moduleIndexPermission(
  USERS_TABS.map((tab) => tab.permission),
)
